/**
 * =============================================================================
 * IDS CRM Web - Inflow stock-integration settings (Settings → Integrations)
 * =============================================================================
 *
 * Basic connection provisioning for the Inflow spares/parts decision
 * (2026-07-03). Stores the API credentials + defaults; the detailed sync
 * workflow (stock levels, drawdown on part fitting) comes later. The API key
 * is write-only: the server returns only a ...last4 hint.
 *
 * Related linkage points: each van carries an Inflow warehouse code
 * (Settings → Vehicles) and each catalogue part an Inflow product ID.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

function InflowSettings() {
  const { authFetch, user } = useAuth();
  const canEdit = user?.role === 'admin';

  const [form, setForm] = useState(null);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const fetchSettings = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/admin/support-settings/inflow');
      if (response.ok) {
        const body = await response.json();
        setForm(body.data);
      }
    } catch (error) {
      console.error('Failed to fetch Inflow settings:', error);
    }
  }, [authFetch]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const save = async () => {
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const payload = {
        enabled: form.enabled,
        api_base_url: form.api_base_url,
        company_id: form.company_id,
        default_warehouse_code: form.default_warehouse_code,
        notes: form.notes
      };
      if (apiKeyInput.trim()) payload.api_key = apiKeyInput.trim();

      const response = await authFetch('/api/v1/admin/support-settings/inflow', {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      const body = await response.json();
      if (response.ok) {
        setForm(body.data);
        setApiKeyInput('');
        setMessage({ text: 'Inflow settings saved', type: 'success' });
      } else {
        setMessage({ text: body.message || 'Failed to save Inflow settings', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Failed to save Inflow settings', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  if (!form) return null;

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Inflow Inventory</h2>
        <div className="section-actions">
          <p className="section-hint">
            Stock integration for spares and parts. Connection details here; each van's
            warehouse number is set on the Vehicles tab, and catalogue parts carry an
            Inflow product ID. Detailed sync workflow to follow.
          </p>
        </div>
      </div>

      {message.text && <div className={`admin-message ${message.type}`}>{message.text}</div>}

      <div className="inflow-settings-grid">
        <label className="inflow-field inflow-toggle">
          <input
            type="checkbox"
            checked={form.enabled}
            disabled={!canEdit}
            onChange={(e) => setField('enabled', e.target.checked)}
          />
          Integration enabled
        </label>

        <div className="inflow-field">
          <label>API base URL</label>
          <input type="text" value={form.api_base_url} disabled={!canEdit}
            onChange={(e) => setField('api_base_url', e.target.value)} />
        </div>

        <div className="inflow-field">
          <label>Company ID</label>
          <input type="text" value={form.company_id} disabled={!canEdit}
            placeholder="Inflow company/tenant identifier"
            onChange={(e) => setField('company_id', e.target.value)} />
        </div>

        <div className="inflow-field">
          <label>API key {form.api_key_set ? `(stored ${form.api_key_hint})` : '(not set)'}</label>
          <input type="password" value={apiKeyInput} disabled={!canEdit}
            placeholder={form.api_key_set ? 'Leave blank to keep current key' : 'Paste Inflow API key'}
            autoComplete="new-password"
            onChange={(e) => setApiKeyInput(e.target.value)} />
        </div>

        <div className="inflow-field">
          <label>Default warehouse code</label>
          <input type="text" value={form.default_warehouse_code} disabled={!canEdit}
            placeholder="e.g. MAIN - used when a van has no warehouse of its own"
            onChange={(e) => setField('default_warehouse_code', e.target.value)} />
        </div>

        <div className="inflow-field inflow-notes">
          <label>Notes</label>
          <textarea rows={2} value={form.notes} disabled={!canEdit}
            onChange={(e) => setField('notes', e.target.value)} />
        </div>
      </div>

      {canEdit && (
        <button className="btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving...' : 'Save Inflow Settings'}
        </button>
      )}
    </div>
  );
}

export default InflowSettings;
